import { PocTypography } from 'commons/components/components-wrapper';
import { InputValidity, Size } from '@one/web-components';
import { DROPDOWN_SEARCH_MIN_CHARACTERS_ALLOWED, MAX_OPTIONS_WITHOUT_SEARCH } from 'core/constants/constants';
import { useTranslation } from 'core/i18n/i18n';
import React, { useState } from 'react';
import { AssistiveText, SelectErrorsLayout } from 'commons/components/form-elements';
import {
    SearchableDropdownCustomSearchInput,
    SearchableDropdownDefaultSearchInput,
    SearchableDropdownList,
} from '../components';
import {
    SearchableDropdownComponent,
    SearchableDropdownLayout,
    SearchableDropdownWrapper,
    SearchBarLayout,
} from '../searchable-dropdown.style';
import { CustomFilterSearch, NestedSelectableOptions, SelectableOptions } from '../searchable-dropdown.type';
import { useSearchBarFocusGuard } from '../hooks/use-search-bar-focus-guard.hook';

interface SearchableDropdownSingleProps {
    name: string;
    label?: string;
    inline?: boolean;
    options: SelectableOptions[] | NestedSelectableOptions[];
    fixedOptions?: SelectableOptions[];
    placeholder: string;
    value?: string;
    searchable?: boolean;
    validity?: InputValidity;
    spaceForError?: boolean;
    floatingAssistiveText?: boolean;
    placeholderAsLabel?: boolean;
    isLoading?: boolean;
    searchPlaceholder?: string;
    disabled?: boolean;
    minWidth?: string;
    customFilter?: CustomFilterSearch;
    required?: boolean;
    size?: Exclude<Size, 'xSmall' | 'large' | 'xLarge'>;
    includeFakeOption?: boolean;
    virtualized?: boolean;
    className?: string;
    onValueChange: (value?: string) => void;
    onClear?: VoidFunction;
    onClickOutside?: VoidFunction;
    onValidity?: (field: string, validity: InputValidity) => void;
}

export const SearchableDropdownSingle: React.FC<SearchableDropdownSingleProps> = (props) => {
    const {
        name,
        label,
        inline = false,
        options,
        fixedOptions = [],
        searchable = options.length > MAX_OPTIONS_WITHOUT_SEARCH,
        placeholder,
        placeholderAsLabel = true,
        value,
        validity,
        spaceForError = false,
        floatingAssistiveText = false,
        isLoading,
        searchPlaceholder,
        minWidth,
        disabled = false,
        customFilter,
        size = 'medium',
        required = false,
        includeFakeOption = false,
        virtualized = false,
        className,
        onValueChange,
        onClear,
        onClickOutside,
        onValidity,
    } = props;
    const isNestedList = options.some((option) => Object.hasOwn(option, 'children'));

    const [defaultFilter, setDefaultFilter] = useState<string>('');
    const [visible, setVisible] = useState<boolean>(false);
    const searchBarRef = useSearchBarFocusGuard();

    const { t } = useTranslation();
    const minCharactersAssistiveText = t('commons.components.searchable-dropdown.search.min-characters', {
        number: DROPDOWN_SEARCH_MIN_CHARACTERS_ALLOWED,
    });

    const getDropdownClassname = () => (visible && value ? 'dropdown-filled' : 'dropdown-empty');

    const handleOnClear = () => {
        setVisible(false);
        onClear?.();
    };

    const handleValueChange = (newValue: string[]) => {
        onValueChange(newValue.at(-1));
    };

    const layoutClassName = ['searchable-dropdown', inline ? 'inline' : 'block', className].filter(Boolean).join(' ');

    return (
        <SearchableDropdownLayout className={layoutClassName}>
            {label ? (
                <PocTypography element="span" variant="subtitle1" className="label block">
                    {label}
                </PocTypography>
            ) : undefined}
            <SearchableDropdownWrapper>
                <SearchableDropdownComponent
                    name={name}
                    data-testid="searchable-dropdown"
                    variant="outlined"
                    size={size}
                    label={placeholderAsLabel ? placeholder : undefined}
                    hideSelectAll
                    value={value}
                    placeholder={placeholder}
                    visible={visible}
                    disabled={!customFilter && (disabled || ![...fixedOptions, ...options].length)}
                    clearButtonProps={{ onclick: handleOnClear }}
                    validity={validity}
                    controlledValidity={validity !== undefined || onValidity !== undefined}
                    required={required}
                    className={getDropdownClassname()}
                    minWidth={minWidth}
                    menuProps={{ noAutoFocus: searchable, listProps: { showSelectorControl: false } }}
                    noClearIcon
                    onValueChange={(ev) => handleValueChange(ev.detail as string[])}
                    onVisibleChange={(ev) => {
                        setVisible(ev.detail);
                    }}
                    onClickOutside={onClickOutside}
                    onValidityChange={(ev) => onValidity?.(name, ev.detail)}
                >
                    {searchable && (
                        <SearchBarLayout ref={searchBarRef} data-testid="searchable-dropdown-search-bar">
                            {customFilter ? (
                                <SearchableDropdownCustomSearchInput
                                    searchFilter={customFilter.filter}
                                    setSearchFilter={customFilter.onChangeFilter}
                                    placeholder={searchPlaceholder}
                                    executeSearch={customFilter.onExecuteSearch}
                                    assistiveTextString={minCharactersAssistiveText}
                                />
                            ) : (
                                <SearchableDropdownDefaultSearchInput
                                    filter={defaultFilter}
                                    setFilter={setDefaultFilter}
                                    placeholder={searchPlaceholder}
                                />
                            )}
                        </SearchBarLayout>
                    )}
                    <SearchableDropdownList
                        selectedValues={value ? [value] : []}
                        options={options}
                        fixedOptions={fixedOptions}
                        defaultFilter={defaultFilter}
                        isLoading={isLoading}
                        size={size}
                        includeFakeOption={includeFakeOption}
                        customFilter={customFilter}
                        isNestedList={isNestedList}
                        visible={visible}
                        virtualized={virtualized}
                    />
                </SearchableDropdownComponent>
                <SelectErrorsLayout
                    slot="assistive-text"
                    className={`${spaceForError ? 'space-for-error' : ''} ${
                        floatingAssistiveText ? 'floating-assistive-text' : ''
                    }`}
                >
                    <AssistiveText validity={validity} />
                </SelectErrorsLayout>
            </SearchableDropdownWrapper>
        </SearchableDropdownLayout>
    );
};
